/**
 * Documents audio player context behavior for ERP Accelerator.
 */

'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type AudioStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';

/**
 * Describes audio metadata data.
 */
export interface AudioMetadata {
  id?: number | string;
  title?: string;
  [key: string]: any;
}

interface AudioPlayerState {
  status: AudioStatus;
  currentTime: number;
  duration: number;
  src?: string;
  metadata?: AudioMetadata | null;
  error?: string | null;
  volume: number;
  muted: boolean;
}

interface AudioPlayerContextValue extends AudioPlayerState {
  play: (src: string, metadata?: AudioMetadata) => Promise<void>;
  pause: () => void;
  resume: () => Promise<void>;
  stop: () => void;
  seek: (seconds: number) => void;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | undefined>(undefined);

const initialState: AudioPlayerState = {
  status: 'idle',
  currentTime: 0,
  duration: 0,
  src: undefined,
  metadata: null,
  error: null,
  volume: 1,
  muted: false,
};

/**
 * Handles audio player provider behavior.
 */
export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<AudioPlayerState>(initialState);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'auto';
    audioRef.current = audio;

    const handleTimeUpdate = () => {
      setState((prev) => ({
        ...prev,
        currentTime: audio.currentTime || 0,
      }));
    };

    const handleLoadedMetadata = () => {
      setState((prev) => ({
        ...prev,
        duration: Number.isFinite(audio.duration) ? audio.duration : prev.duration,
      }));
    };

    const handlePlaying = () => {
      setState((prev) => ({
        ...prev,
        status: 'playing',
        error: null,
      }));
    };

    const handlePause = () => {
      setState((prev) => ({
        ...prev,
        status: audio.currentTime > 0 && audio.currentTime < audio.duration ? 'paused' : prev.status,
      }));
    };

    const handleEnded = () => {
      setState((prev) => ({
        ...prev,
        status: 'ended',
        currentTime: audio.duration || prev.currentTime,
      }));
    };

    const handleError = () => {
      const err = audio.error;
      const message =
        (err && (err as any)?.message) ||
        (err ? `Audio playback error (code ${err.code})` : 'Audio playback error');
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: message,
      }));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.volume !== state.volume) {
      audio.volume = state.volume;
    }
    if (audio.muted !== state.muted) {
      audio.muted = state.muted;
    }
  }, [state.volume, state.muted]);

  const play = async (src: string, metadata?: AudioMetadata) => {
    const audio = audioRef.current;
    if (!audio) return;
    const sameSource = audio.src === src;
    try {
      if (!sameSource) {
        audio.src = src;
      }
      audio.currentTime = 0;
      const playPromise = audio.play();
      setState((prev) => ({
        ...prev,
        status: 'loading',
        currentTime: 0,
        duration: sameSource ? prev.duration : 0,
        src,
        metadata: metadata || null,
        error: null,
      }));
      await playPromise;
    } catch (err: any) {
      const message = err?.message || 'Failed to start playback';
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: message,
      }));
      throw err;
    }
  };

  const resume = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (state.status === 'playing') return;
    try {
      await audio.play();
    } catch (err: any) {
      const message = err?.message || 'Unable to resume playback';
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: message,
      }));
      throw err;
    }
  };

  const pause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  };

  const stop = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setState((prev) => ({
      ...prev,
      status: 'idle',
      currentTime: 0,
      duration: 0,
      src: undefined,
      metadata: null,
    }));
  };

  const seek = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const clamped = Math.max(0, Math.min(seconds, audio.duration || seconds));
    try {
      audio.currentTime = clamped;
      setState((prev) => ({
        ...prev,
        currentTime: clamped,
      }));
    } catch {}
  };

  const setVolume = (volume: number) => {
    const safeVolume = Math.min(1, Math.max(0, volume));
    setState((prev) => ({
      ...prev,
      volume: safeVolume,
    }));
  };

  const setMuted = (muted: boolean) => {
    setState((prev) => ({
      ...prev,
      muted,
    }));
  };

  const value = useMemo<AudioPlayerContextValue>(
    () => ({
      ...state,
      play,
      pause,
      resume,
      stop,
      seek,
      setVolume,
      setMuted,
    }),
    [state],
  );

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

/**
 * Handles use audio player behavior.
 */
export const useAudioPlayer = (): AudioPlayerContextValue => {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return ctx;
};
